import * as React from "react"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"
import { FilterChip } from "@/components/ui/filter-chip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/*
  ChipSelect — a FilterChip that opens a picker: "Project ⌄" when unset, "Project: NOBU ×" when set.

    <ChipSelect label="Supplier" value={supplier} onChange={setSupplier}
      options={[{ value: 'dutco', label: 'Dutco', count: 4 }, { value: '__none__', label: 'Supplier missing', muted: true }]} />

    // several values (wave 2): the list stays open, each row is a check box
    <ChipSelect multiple label="Status" value={statuses} onChange={setStatuses} options={statusOptions} />
    → chip reads "Status: Submitted +2"; × clears all; value is always an array.

  options: [{ value, label, count?, muted? }] — a search box shows above 8 options (or with
  `searchable`; `searchable={false}` hides it). Single mode: picking the selected option again clears
  it. ↓ from the search box and ↑/↓ in the list move between options.
  Props: label, value, options, onChange(value | null | array), multiple, icon, variant ('select'
  chevron | 'add' dashed "+ Label"), emptyText, allLabel (an "All …" row at the top, single mode),
  footer (note under the list), align, popoverClassName (width), searchable, defaultOpen,
  open / onOpenChange (controlled), testId (on the chip), className (on the chip, e.g. a taller phone chip).
*/
function OptionRow({ active, label, count, muted, multiple, onSelect }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onClick={onSelect}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-lg px-2.5 text-left text-[13px] outline-none hover:bg-surface-hover focus-visible:bg-surface-hover",
        active ? "font-semibold text-brand-ink" : muted ? "text-ink-3" : "text-ink"
      )}
    >
      {multiple ? (
        <span
          className={cn(
            "grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border-[1.5px] text-white",
            active ? "border-brand bg-brand" : "border-[color:var(--ink-4)] bg-surface"
          )}
          aria-hidden="true"
        >
          {active ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
        </span>
      ) : (
        <span className="grid h-4 w-4 shrink-0 place-items-center">{active ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}</span>
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && count !== null ? <span className="shrink-0 text-[11.5px] font-semibold tabular-nums text-ink-3">{formatNumber(count, { maxDecimals: 0 })}</span> : null}
    </button>
  )
}

function moveFocus(container, step) {
  const items = [...(container?.querySelectorAll('[role="option"]') || [])]
  if (!items.length) return
  const index = items.indexOf(document.activeElement)
  const next = index < 0 ? (step > 0 ? 0 : items.length - 1) : Math.min(items.length - 1, Math.max(0, index + step))
  items[next]?.focus()
}

function ChipSelect({
  label,
  value,
  options = [],
  onChange,
  multiple = false,
  icon,
  variant = "select",
  emptyText = "Nothing to pick yet",
  testId,
  align = "start",
  allLabel,
  footer,
  popoverClassName = "w-72",
  searchable,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  className,
}) {
  const [openState, setOpenState] = React.useState(defaultOpen)
  const open = openProp ?? openState
  const [query, setQuery] = React.useState("")
  const listRef = React.useRef(null)
  const selectedValues = multiple ? (Array.isArray(value) ? value : value ? [value] : []) : []
  const selected = multiple ? null : options.find((option) => option.value === value)
  const showSearch = searchable ?? options.length > 8
  const visible = React.useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle ? options.filter((option) => String(option.label).toLowerCase().includes(needle)) : options
  }, [options, query])

  const setOpen = (next) => {
    if (openProp === undefined) setOpenState(next)
    onOpenChange?.(next)
    if (!next) setQuery("")
  }

  const labelOf = (optionValue) => options.find((option) => option.value === optionValue)?.label ?? optionValue
  let chipValue = null
  if (multiple) {
    if (selectedValues.length === 1) chipValue = labelOf(selectedValues[0])
    else if (selectedValues.length > 1) chipValue = `${labelOf(selectedValues[0])} +${selectedValues.length - 1}`
  } else if (value) {
    chipValue = selected?.label ?? value
  }

  const toggle = (optionValue) => {
    if (!multiple) {
      onChange(optionValue === value ? null : optionValue)
      setOpen(false)
      return
    }
    const has = selectedValues.includes(optionValue)
    onChange(has ? selectedValues.filter((item) => item !== optionValue) : [...selectedValues, optionValue])
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FilterChip
          label={label}
          value={chipValue}
          onClear={chipValue !== null ? () => onChange(multiple ? [] : null) : undefined}
          icon={icon}
          variant={variant}
          className={className}
          data-testid={testId}
        />
      </PopoverTrigger>
      <PopoverContent align={align} className={cn("rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay", popoverClassName)}>
        {showSearch ? (
          <div className="p-1 pb-1.5">
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault()
                  moveFocus(listRef.current, 1)
                }
              }}
              placeholder={`Find ${String(label).toLowerCase()}…`}
              aria-label={`Find ${String(label).toLowerCase()}`}
              className="h-8 w-full rounded-lg border border-line bg-surface px-2.5 text-[13px] text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-soft-border)]"
            />
          </div>
        ) : null}
        <div
          ref={listRef}
          className="max-h-72 overflow-y-auto"
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault()
              moveFocus(listRef.current, event.key === "ArrowDown" ? 1 : -1)
            }
          }}
        >
          {allLabel && !multiple ? (
            <OptionRow active={!value} label={allLabel} onSelect={() => { onChange(null); setOpen(false) }} />
          ) : null}
          {visible.length ? visible.map((option) => (
            <OptionRow
              key={option.value}
              multiple={multiple}
              active={multiple ? selectedValues.includes(option.value) : option.value === value}
              label={option.label}
              muted={option.muted}
              count={option.count}
              onSelect={() => toggle(option.value)}
            />
          )) : (
            <p className="px-2.5 py-3 text-[12.5px] text-ink-3">{query ? "No matches" : emptyText}</p>
          )}
        </div>
        {multiple && selectedValues.length ? (
          <div className="mt-1 flex items-center justify-between border-t border-line-2 px-1 pt-1.5">
            <button type="button" onClick={() => onChange([])} className="h-7 rounded-md px-1.5 text-[12.5px] font-semibold text-ink-3 hover:bg-surface-hover hover:text-ink">
              Clear {formatNumber(selectedValues.length, { maxDecimals: 0 })}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="h-7 rounded-md px-2 text-[12.5px] font-semibold text-brand-ink hover:bg-surface-hover">
              Done
            </button>
          </div>
        ) : null}
        {footer ? <div className="mt-1 border-t border-line-2 px-2.5 pb-1 pt-2 text-[12px] text-ink-3">{footer}</div> : null}
      </PopoverContent>
    </Popover>
  )
}

export { ChipSelect }
